<script lang="ts">
export default {
  name: 'WholesalerGroupView',
};
</script>

<script setup lang="ts">
import { computed, ComputedRef, onMounted, ref } from 'vue';
import { WholesalerGroup } from '@/types/WholesalerGroup';
import { favoriteService, wholesalerGroupService } from '@/services/api';
import FavoriteIndicator from '@/components/FavoriteIndicator.vue';
import PageSpinner from '@/components/v2/PageSpinner.vue';
import { useRoute } from 'vue-router';
import { registerResizeItem } from '@/utilities/resize';
import { useEntityTabs } from '@/composables/useEntityTabs';
import type { TabConfig } from '@/types/EntityTabs';
import Tabs from '@/components/v2/Tabs.vue';
import { useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();
const { getTabs } = useEntityTabs();

// ------------
//     data
// ------------

const loading = ref(true);
const wholesalerGroup = ref({} as WholesalerGroup);
const favoriteProgress = ref(false);
const wholesalerTabsComputedRef = ref<ComputedRef<TabConfig[]> | null>(null);
const wholesalerTabs = computed(() => {
  const cref = wholesalerTabsComputedRef.value as ComputedRef<TabConfig[]> | null;
  return cref?.value ?? [];
});

// ------------
//    methods
// ------------

const toggleFavorite = async () => {
  favoriteProgress.value = true;
  if (!wholesalerGroup.value.favorite) {
    await favoriteService.remove('wholesalerGroup', wholesalerGroup.value.id);
    wholesalerGroup.value.favorite = false;
  } else {
    await favoriteService.create('wholesalerGroup', wholesalerGroup.value.id);
    wholesalerGroup.value.favorite = true;
  }
  favoriteProgress.value = false;
};

const refresh = async () => {
  loading.value = true;
  const id = route.params.id as string;
  wholesalerGroup.value =
    route.meta?.routeGroup === 'WholesalerGroupCustom'
      ? await wholesalerGroupService.customGroupById(id)
      : await wholesalerGroupService.findById(id);

  await loadWholesalerTabs();
  loading.value = false;
};

const loadWholesalerTabs = async () => {
  try {
    const id = route.params.id as string;
    wholesalerTabsComputedRef.value = route.meta?.routeGroup === 'WholesalerGroupCustom'
      ? await getTabs('customWholesaler', id)
      : await getTabs('wholesaler', id);
  } catch (error) {
    console.error('Error loading wholesaler tabs:', error);
    wholesalerTabsComputedRef.value = computed(() => []);
  }
};

// ------------
//    computed
// ------------

const isCustomGroup = computed(() => {
  return route.meta?.routeGroup === 'WholesalerGroupCustom';
});

const wholesalerTabItems = computed(() =>
  wholesalerTabs.value.map((t) => ({ value: t.key, label: t.label }))
);
const currentTab = computed(() => {
  const parts = route.path.split('/').filter(Boolean);
  const last = parts[parts.length - 1] ?? '';
  const tabKeys = wholesalerTabs.value.map((t) => t.key);
  return tabKeys.includes(last) ? last : '';
});
function onWholesalerTabChange(val: string) {
  const tab = wholesalerTabs.value.find((t) => t.key === val);
  if (tab) router.push(tab.route);
}

// ------------
//   lifecycle
// ------------

onMounted(async () => {
  registerResizeItem({ name: 'wholesaler-header', selector: 'wholesaler-header' });
  registerResizeItem({ name: 'wholesaler-tabs', selector: 'wholesaler-tabs' });
  await refresh();
});
</script>

<template>
  <div>
    <PageSpinner v-if="loading" />
    <div v-else>
      <div id="wholesaler-header" class="inner">
        <div class="flex items-center gap-2.5">
          <h1>{{ wholesalerGroup.name }}</h1>
          <div v-if="!isCustomGroup" class="actions flex items-center">
            <FavoriteIndicator
              v-model="wholesalerGroup.favorite"
              @update:model-value="toggleFavorite"
              :loading="favoriteProgress" />
          </div>
        </div>
      </div>
      <div class="w-full">
        <Tabs
          id="wholesaler-tabs"
          :model-value="currentTab"
          :items="wholesalerTabItems"
          class="tab-bar uppercase w-full"
          variant="lifted"
          @update:model-value="onWholesalerTabChange" />
      </div>
      <router-view></router-view>
    </div>
  </div>
</template>
